import { useAuthStore } from '@caradasstore/AuthStore'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useUserStore } from '@caradasstore/UserStore'
import { UserForm, UserInfo } from '@components/user'
import { AdasBox, AdasPagination, AdasSelect, AdasStack, AdasTable, AdasTypography } from '@components/wrapper-components'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getAllUserByCcid, getCalibrationCenter, getClient, getClientUserQuota, getClients } from '../../../api/api'
import { TABLE_HEADER_STYLE, USER_TABLE_HEADERS } from '../../../constants'
import { Logger } from '../../../logger'
import { scrollPageTop } from 'utils/useScrollTop'
import { ItemPerPageSelector, SearchOrAddData } from '@components/common'

// Client users are managed in two places (CAR-3869): Settings → Team & roles, which lists
// everyone and can narrow to center staff, all client users or one client's users
// ('shop:<client id>'); and a client's "Client users" tab (clientId set), which lists, adds
// and edits only that client's users.
const SHOP_PREFIX = 'shop:'
const shopIdOf = (filter) => (filter.startsWith(SHOP_PREFIX) ? Number(filter.slice(SHOP_PREFIX.length)) : null)
const USER_TABLE_HEADERS_WITH_SHOP = [
  ...USER_TABLE_HEADERS.slice(0, 4),
  { id: 'client_name', label: 'Client', minWidth: '140px' },
  ...USER_TABLE_HEADERS.slice(4)
]
// Every row of a client's users has the Client role, so that tab drops the Role column.
const CLIENT_USER_TABLE_HEADERS = USER_TABLE_HEADERS.filter((header) => header.id !== 'role')

export const UserQueue = ({ clientId }) => {
  const isClientView = !!clientId
  const location = useLocation()
  const [userFilter, setUserFilter] = useState(() => {
    // ?clientId=7 (e.g. from Settings?section=team&clientId=7) opens on that client's users.
    const fromUrl = Number(new URLSearchParams(location?.search || '').get('clientId'))
    return Number.isFinite(fromUrl) && fromUrl > 0 ? `${SHOP_PREFIX}${fromUrl}` : 'all'
  })
  const [clients, setClients] = useState([])
  const [clientName, setClientName] = useState()
  const [searchInput, setSearchInput] = useState('')
  const [currentUserList, setCurrentUserList] = useState([])
  const [isUserInfo, setIsUserInfo] = useState(false)
  const [isAddUser, setIsAddUser] = useState(false)
  // The location's client-user cap ({limit, used, exhausted}); null while unknown or when not viewing client users.
  const [clientQuota, setClientQuota] = useState(null)
  const [userData, setUserData] = useState({})
  const [sort, setSort] = useState('ASC')
  const authCtx = useAuthStore()

  const ccId = useCenterStore((store) => store.ccId)
  const setCenterDetail = useCenterStore((store) => store.setCenterDetail)
  const centerDetail = useCenterStore((store) => store.centerDetail)
  const currentUser = useUserStore((store) => store.currentUser)
  const setUserList = useCenterStore((store) => store.setUserList)
  const [pageNo, setPageNo] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [isActive, setIsActive] = useState(true)
  const [search, setSearch] = useState('')

  const paginationHandler = (_event, page) => {
    setPageNo(page - 1)
    scrollPageTop()
  }

  const pageSizeHandler = (event) => {
    setPageNo(0)
    setPageSize(event.target.value)
    scrollPageTop()
  }

  const searchInputHandler = useCallback((e) => {
    setSearchInput(e.target.value)
  }, [])

  const clickSortHandler = useCallback(
    (id) => {
      if (id === 'name') setSort(sort === 'ASC' ? 'DESC' : 'ASC')
    },
    [sort]
  )

  const fetchCC = async () => {
    try {
      const response = await getCalibrationCenter({
        ccid: ccId
      })
      setCenterDetail(response.data)
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'userQueue', ccId: ccId } })
    }
  }

  useEffect(() => {
    const performSearch = () => {
      setSearch(searchInput)
    }

    // Set a delay to perform the search 500ms after typing stops
    const timeoutId = setTimeout(performSearch, 1000)
    return () => clearTimeout(timeoutId)
  }, [searchInput])

  // Team & roles: the center's clients, for the filter, the Client column and the user profile.
  useEffect(() => {
    const ccid = centerDetail.id ? centerDetail.id : ccId
    if (isClientView || !ccid) {
      return
    }
    getClients({ ccid })
      .then((response) => {
        if (response?.status === 200) {
          setClients(response.data || [])
        }
      })
      .catch((error) => Logger.error({ message: error, payload: { file: 'userQueue', method: 'getClients', ccid } }))
  }, [isClientView, centerDetail?.id, ccId])

  const clientNames = useMemo(() => Object.fromEntries(clients.map((client) => [client.id, client.name])), [clients])
  const filterShopId = isClientView ? Number(clientId) : shopIdOf(userFilter)
  const showsShopUsers = !isClientView && (userFilter === 'client' || filterShopId !== null)

  // The client tab: the client's name, for the Client line of a user's profile.
  useEffect(() => {
    if (!clientId) {
      return
    }
    getClient({ clientId })
      .then((response) => {
        if (response?.status === 200) {
          setClientName(response.data?.name)
        }
      })
      .catch((error) => Logger.error({ message: error, payload: { file: 'userQueue', method: 'getClient', clientId } }))
  }, [clientId])

  const fetchUserList = useCallback(async () => {
    const userListResponse = await getAllUserByCcid({
      ccid: centerDetail.id ? centerDetail.id : ccId,
      isActive,
      pageNo,
      pageSize,
      sortBy: 'name',
      sort: sort,
      search,
      userType: isClientView ? 'client' : userFilter === 'staff' || userFilter === 'client' ? userFilter : undefined,
      clientId: filterShopId || undefined
    })

    setTotalPages(userListResponse.data.totalPages)
    if (userListResponse.status === 200) {
      if (currentUser.is_owner || currentUser.is_admin) {
        setCurrentUserList(userListResponse.data.userDisplayDtos.filter((user) => !user.is_admin))
      } else if (currentUser.is_manager) {
        setCurrentUserList(userListResponse.data.userDisplayDtos.filter((user) => !user.is_admin && !user.is_owner))
      }

      // The center's users are a global state (assignee pickers); a list narrowed to client
      // users is not.
      if (!isClientView && (userFilter === 'all' || userFilter === 'staff')) {
        setUserList(userListResponse.data.userDisplayDtos.filter((user) => user.is_active)) // only active user should be here since its a global state
      }
    }
  }, [
    ccId,
    pageNo,
    pageSize,
    isActive,
    sort,
    centerDetail?.id,
    currentUser?.is_admin,
    currentUser?.is_manager,
    currentUser?.is_owner,
    search,
    isClientView,
    userFilter,
    filterShopId
  ])

  useEffect(() => {
    fetchUserList()
  }, [fetchUserList])

  const userInfoHandler = useCallback((props) => {
    setUserData(props)
    setIsUserInfo(true)
  }, [])

  const tableRows = useMemo(
    () =>
      showsShopUsers
        ? currentUserList.map((user) => ({ ...user, client_name: user.client_id ? clientNames[user.client_id] || '—' : '—' }))
        : currentUserList,
    [showsShopUsers, currentUserList, clientNames]
  )

  const filterOptions = useMemo(
    () => [
      { id: 'all', name: 'All users' },
      { id: 'staff', name: 'Center staff' },
      { id: 'client', name: 'Client users' },
      ...(clients.length > 0 ? [{ divider: true }, { heading: 'Clients' }] : []),
      ...clients.map((client) => ({ id: `${SHOP_PREFIX}${client.id}`, name: client.name }))
    ],
    [clients]
  )

  // Adding from a client view (the client tab, or Team & roles narrowed to client users)
  // starts as a client user - of that client, when there is one.
  const newUserDefaults = useMemo(
    () => ({
      is_active: true,
      ...(isClientView || showsShopUsers ? { is_client_user: true } : {}),
      ...(filterShopId ? { client_id: filterShopId } : {})
    }),
    [isClientView, showsShopUsers, filterShopId]
  )

  // Self sign-up locations may have a limited number of client users; the cap applies to the
  // client-user views only, so staff lists never ask. Re-read after every add/edit (a deactivation
  // frees a seat, a reactivation takes one).
  const viewingClientUsers = isClientView || showsShopUsers
  const fetchClientQuota = useCallback(() => {
    if (!ccId || !viewingClientUsers) {
      setClientQuota(null)
      return
    }
    // The cap is per body shop (0173): with a shop selected, its usage; otherwise the plan's cap alone.
    getClientUserQuota({ ccid: ccId, clientId: filterShopId || undefined }).then((quota) => setClientQuota(quota || null))
  }, [ccId, viewingClientUsers, filterShopId])

  useEffect(() => {
    fetchClientQuota()
  }, [fetchClientQuota])

  const addBlocked = viewingClientUsers && !!clientQuota?.perClient && !!clientQuota?.exhausted

  const onUserAddOrUpdateSuccessHandler = useCallback(() => {
    fetchUserList()
    fetchClientQuota()
    if (!isClientView) {
      fetchCC() //Fetch store details since its a global state
    }
  }, [fetchUserList, fetchClientQuota, fetchCC, isClientView])

  const showingRange = useMemo(() => {
    const count = currentUserList.length
    if (count === 0) return 'No results'
    const start = pageNo * pageSize + 1
    const end = pageNo * pageSize + count
    const total = totalPages > 0 ? Math.max(totalPages * pageSize, end) : end
    return `Showing ${start}-${end} of ${total}`
  }, [currentUserList.length, pageNo, pageSize, totalPages])

  const userKind = isClientView ? 'client' : undefined

  return (
    <AdasBox>
      {isUserInfo && (
        <UserInfo
          open={isUserInfo}
          allowDelete={true}
          updateClientListHandler={onUserAddOrUpdateSuccessHandler}
          userInfo={userData}
          clientName={isClientView ? clientName : userData?.client_id ? clientNames[userData.client_id] : undefined}
          userKind={userKind}
          clients={isClientView ? undefined : clients}
          closeDrawer={() => setIsUserInfo(false)}
        />
      )}
      {isAddUser && (
        <UserForm
          open={isAddUser}
          isAddUser={true}
          onSaveSuccess={onUserAddOrUpdateSuccessHandler}
          clibrationCenterId={authCtx?.user}
          close={() => setIsAddUser(false)}
          userData={newUserDefaults}
          userKind={userKind}
          clients={isClientView ? undefined : clients}
        />
      )}

      {/* Toolbar row — sits OUTSIDE the table card */}
      <AdasBox className='flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3 mb-4'>
        <AdasBox className='flex-1 max-w-[720px]'>
          <SearchOrAddData
            searchInput={searchInput}
            searchInputHandler={searchInputHandler}
            searchPlaceholder='Search...'
            onAddClick={() => setIsAddUser(true)}
            variant={addBlocked ? 'withAddButtonDisabled' : 'withAddButton'}
          />
        </AdasBox>
        <AdasBox className='flex items-center gap-2 shrink-0'>
          {viewingClientUsers && clientQuota?.limited && (
            <span
              data-testid='client-user-quota'
              title={
                addBlocked
                  ? 'Limit reached — deactivate a client user to add another.'
                  : clientQuota.tierName
                    ? `From the ${clientQuota.tierName} plan`
                    : undefined
              }
              className={`inline-flex h-11 items-center rounded-lg border px-4 text-[13px] font-semibold ${
                addBlocked ? 'border-[#F5C2C2] bg-[#FDECEC] text-[#7A271A]' : 'border-border bg-white text-foreground'
              }`}
            >
              {clientQuota.perClient
                ? `${clientQuota.used} of ${clientQuota.limit} client users${addBlocked ? ' · limit reached' : ''}`
                : `Up to ${clientQuota.limit} client users per body shop`}
            </span>
          )}
          {!isClientView && (
            <AdasBox className='w-[240px]'>
              <AdasSelect
                name='userFilter'
                value={userFilter}
                onChange={(e) => {
                  setPageNo(0)
                  setUserFilter(String(e.target.value))
                }}
                options={filterOptions}
                placeholder='All users'
              />
            </AdasBox>
          )}
          <button
            type='button'
            onClick={() => setIsActive(true)}
            className={`h-11 px-5 rounded-lg text-[13px] font-semibold tracking-wide transition-colors ${
              isActive ? 'bg-secondary text-white' : 'border border-border bg-white text-foreground hover:bg-muted'
            }`}
          >
            ACTIVE
          </button>
          <button
            type='button'
            onClick={() => setIsActive('')}
            className={`h-11 px-5 rounded-lg text-[13px] font-semibold tracking-wide transition-colors ${
              isActive === '' ? 'bg-secondary text-white' : 'border border-border bg-white text-foreground hover:bg-muted'
            }`}
          >
            ALL
          </button>
        </AdasBox>
      </AdasBox>

      {/* Table card */}
      <AdasBox className='rounded-xl border border-border bg-white overflow-hidden'>
        {tableRows.length > 0 ? (
          <AdasTable
            containerProps={{ component: AdasBox }}
            tableProps={{
              sx: {
                '.MuiTableCell-root': {
                  border: 'none',
                  padding: '14px 20px',
                  fontSize: '0.9375rem'
                },
                '.MuiTableRow-root': {
                  borderBottom: '1px solid hsl(var(--border))'
                },
                'aria-label': 'user table'
              }
            }}
            columns={isClientView ? CLIENT_USER_TABLE_HEADERS : showsShopUsers ? USER_TABLE_HEADERS_WITH_SHOP : USER_TABLE_HEADERS}
            sortField='name'
            sortDirection={sort}
            onSort={clickSortHandler}
            headerStyle={TABLE_HEADER_STYLE}
            data={tableRows}
            onSelect={userInfoHandler}
          />
        ) : (
          <AdasBox className='py-12 text-center text-[14px] text-muted-foreground'>
            {isClientView ? 'No client users yet.' : 'No users found.'}
          </AdasBox>
        )}

        <AdasBox className='flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-border'>
          <AdasTypography className='text-[13px] text-muted-foreground'>{showingRange}</AdasTypography>
          <AdasBox className='flex items-center gap-4'>
            {totalPages > 1 && (
              <AdasStack spacing={2}>
                <AdasPagination onChange={paginationHandler} count={totalPages} shape='rounded' />
              </AdasStack>
            )}
            <ItemPerPageSelector pageSizeHandler={pageSizeHandler} pageSize={pageSize} />
          </AdasBox>
        </AdasBox>
      </AdasBox>
    </AdasBox>
  )
}

UserQueue.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}
